import happyAnimation from "@animations/happy";
import neutralAnimation from "@animations/neutral";
import sadAnimation from "@animations/sad";
import lottie, { type AnimationItem } from "lottie-web";
import { useEffect, useRef } from "react";
import type { AnimationType } from "./Chatbot";

const ANIMATIONS = {
  happy: happyAnimation,
  sad: sadAnimation,
  neutral: neutralAnimation,
} as const;

interface AnimationDisplayProps {
  animationType: AnimationType | null;
}

function AnimationDisplay({ animationType }: AnimationDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!containerRef.current || !animationType) {
      return;
    }

    // Clean up previous animation
    if (animationRef.current) {
      animationRef.current.destroy();
      animationRef.current = null;
    }

    // Clear container
    containerRef.current.innerHTML = "";

    // Load new animation
    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: ANIMATIONS[animationType],
    });

    // Cleanup function
    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [animationType]);

  return (
    <div
      ref={containerRef}
      className="flex h-[200px] items-center justify-center rounded-xl border-2 border-dashed border-cyan-200 bg-linear-to-br from-cyan-50 to-teal-50 text-[15px] font-medium text-teal-600 transition-all duration-300 not-empty:border-0 not-empty:bg-transparent empty:border-2 dark:border-slate-600 dark:from-slate-800 dark:to-slate-900 dark:text-cyan-400"
      role="img"
      aria-label="Animation display area"
    />
  );
}

export default AnimationDisplay;
